import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";
import {useNavigate} from "react-router";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
import {PageForm} from "@/components/PageForm/PageForm.tsx";
import {
  GQL_INCIDENTS,
  GQL_INCIDENTS_COUNTS,
  GQL_STATUS_PAGE_QUERY,
  GQL_STATUSPAGE_INCIDENTS,
  GQL_UPDATE_INCIDENT,
  UpdateIncidentResult,
} from "@/lib/queries/statuspages.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {Incident, IncidentType} from "@/lib/types/statuspages.ts";
import {GlobalSettingsForm} from "@/pages/incidents/components/incident-edit/components/GlobalSettingsForm.tsx";
import {IncidentUpdatesForm} from "@/pages/incidents/components/incident-edit/components/updates/IncidentUpdatesForm.tsx";
import {IncidentEditFormData} from "@/pages/incidents/components/incident-edit/IncidentForm.types.ts";
import {
  isAttachedComponent,
  isNotificationIncident,
  updateIncidentFormDefaultValuesBuilder,
  updateIncidentFormSchemaBuilder,
} from "@/pages/incidents/components/incident-edit/IncidentForm.utils.tsx";
import {setServerErrors} from "@/pages/vault/vaultForm/CredentialFormUtils.ts";

export interface IncidentFormProps {
  statusPageId?: number;
  defaultIncidentType: IncidentType;
  incident?: Incident;
  onSuccess?: () => void;
  redirectTo?: string;
  disableStatusPageSelect?: boolean;
}

export const IncidentForm = ({
  statusPageId,
  incident,
  defaultIncidentType,
  onSuccess,
  redirectTo,
  disableStatusPageSelect,
}: IncidentFormProps) => {
  const navigate = useNavigate();
  const {user} = useCurrentUserContext();
  const timezone = user!.account!.timezone;
  const incidentType = incident?.incident_type || defaultIncidentType || IncidentType.INCIDENT;
  const isScheduledMaintenance = incidentType === IncidentType.SCHEDULED_MAINTENANCE;
  const submitBtnText = incident?.id
    ? "Save Changes"
    : incidentType === IncidentType.INCIDENT
      ? "Create Incident"
      : "Schedule Maintenance";

  const UpdateIncidentFormSchema = updateIncidentFormSchemaBuilder({
    isScheduledMaintenance,
    timezone,
  });

  const methods = useForm<IncidentEditFormData>({
    defaultValues: updateIncidentFormDefaultValuesBuilder({
      incident,
      timezone,
      defaultIncidentType,
      statusPageId,
      user: user!,
    }),
    reValidateMode: "onChange",
    mode: "onChange",
    resolver: yupResolver<IncidentEditFormData>(UpdateIncidentFormSchema),
  });

  const [updateIncident, {data}] = useMutation<UpdateIncidentResult>(GQL_UPDATE_INCIDENT, {
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_INCIDENTS, GQL_INCIDENTS_COUNTS, GQL_INCIDENTS],
    context: {noToast: true},
  });

  const incidentFormServerErrors = data?.updateIncident?.success
    ? (data?.updateIncident.errors as ServerErrors)
    : undefined;

  const [serverErrorsState, setServerErrorsState] = useState<ServerErrors | undefined>(incidentFormServerErrors);

  useEffect(() => {
    if (serverErrorsState) {
      setServerErrors(serverErrorsState, methods.setError, "");
    }
  }, [serverErrorsState, methods.setError]);

  const onSubmit = async (formData: IncidentEditFormData) => {
    const {statuspage_id, affected_components, incident_type, updates, ...payload} = formData;
    // a Notification attaches no components, but must not drop the ones an earlier update recorded
    const notificationIncident = isNotificationIncident(updates);
    const affectedComponents = affected_components
      ?.filter((ac) => (notificationIncident ? !!ac.obj_id : isAttachedComponent(ac)))
      .map((ac) => ({id: ac.id, obj_id: ac.obj_id, status: ac.status}));
    const updatesClear = updates.map((up) => ({
      description: up.description,
      description_html: up.description_html,
      id: up.id,
      incident_state: up.incident_state,
    }));

    const ends_at: Date | null = formData.ends_at ? formData.ends_at : null;
    const response = await updateIncident({
      variables: {
        statusPageID: statuspage_id,
        data: {
          ...payload,
          ends_at: ends_at,
          updates: updatesClear,
          incident_type: incident_type,
          affected_components: affectedComponents,
          // a chosen status is applied on save, except on a Notification, which leaves components alone
          update_component_status: !notificationIncident,
        },
      },
    });
    if (response?.data?.updateIncident?.success) {
      if (formData.notify_subscribers) {
        toast.success(<>An update was queued to be sent to the page subscribers in approximately 2 minutes.</>);
      }
      if (onSuccess) {
        onSuccess();
      } else if (redirectTo) {
        navigate(redirectTo);
      }
    } else {
      setServerErrorsState(response.data?.updateIncident?.errors);
    }
  };

  const handleCancel = () => {
    if (redirectTo) {
      navigate(redirectTo);
    } else {
      navigate(-1);
    }
  };

  const title = incident?.id
    ? `Edit ${isScheduledMaintenance ? "Scheduled Maintenance" : "Incident"}`
    : isScheduledMaintenance
      ? "Schedule Maintenance"
      : "Create Incident";

  return (
    <FormProvider {...methods}>
      <PageForm
        title={title}
        formId="incident-form"
        submitText={submitBtnText}
        goBack={handleCancel}
        serverErrorsState={serverErrorsState}
      >
        <form
          id="incident-form"
          noValidate={true}
          onSubmit={(e) => {
            e.preventDefault();
            methods.handleSubmit(onSubmit)(e);
          }}
        >
          <GlobalSettingsForm disableStatusPageSelect={disableStatusPageSelect} />

          <Divider className="my-5" />

          <IncidentUpdatesForm />
        </form>
      </PageForm>
    </FormProvider>
  );
};
